import type { RelatedPersonRecord } from '@models/related-person';

import { capitalize, formatAddress, formatName } from '@utils/format';

import { ContactType } from 'generated/types';
import type { ContactModel } from 'generated/types';

import type { ContactCardData } from './ContactCard';

function getCareTeamSortCategory(contact: ContactModel): number {
  const isPrimaryCare = contact.role_name
    .toLowerCase()
    .includes('primary care');
  if (isPrimaryCare) return 0;
  if (contact.resourceType === ContactType.Organization) return 2;
  return 1;
}

export function compareCareTeamContacts(
  a: ContactModel,
  b: ContactModel
): number {
  const categoryDiff = getCareTeamSortCategory(a) - getCareTeamSortCategory(b);
  if (categoryDiff !== 0) return categoryDiff;

  const roleCompare = a.role_name.localeCompare(b.role_name);
  if (roleCompare !== 0) return roleCompare;

  const nameA = formatName(a.prefix, a.first_name, a.last_name) ?? '';
  const nameB = formatName(b.prefix, b.first_name, b.last_name) ?? '';
  return nameA.localeCompare(nameB);
}

export function mapCareTeamContact(contact: ContactModel): ContactCardData {
  return {
    id: contact.id ?? '',
    name:
      formatName(contact.prefix, contact.first_name, contact.last_name) ?? '',
    chipLabel: contact.role_name,
    chipVariant: contact.resourceType ?? undefined,
    subtitle: contact.resourceType ?? undefined,
    phone: contact.phone,
    email: contact.email,
    location:
      contact.address ||
      contact.city ||
      contact.region ||
      contact.country ||
      contact.postal_code
        ? formatAddress(
            [contact.address ?? null, contact.address2 ?? null],
            contact.city,
            contact.postal_code,
            contact.region,
            contact.country
          )
        : null,
  };
}

export function mapRelatedPerson(person: RelatedPersonRecord): ContactCardData {
  return {
    id: person.id ?? '',
    name: person.name ?? '',
    chipLabel:
      person.relationship
        ?.map(({ role_name }) => capitalize(role_name))
        .join(', ') ?? '',
    phone: person.phone,
    email: person.email,
    location: person.primaryAddress,
  };
}
